<template>
	<a-modal
		class="g-pay-audio-library-edit-modal"
		:visible="visible"
		title="音频付费编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<a-form layout="vertical" class="g-pay-audio-library-edit-form">
				<a-form-item label="标题">
					<a-input v-model:value="form.title" placeholder="例如 我的精选歌单" />
				</a-form-item>
			</a-form>
			<PayMediaEditForm
				:form="form"
				mode="count"
				:max-count="form.items.length"
				:show-title="false"
				:show-subtitle="true"
				subtitle-placeholder="例如 共 6 首精选音频"
			/>

			

			<div class="g-pay-audio-library-edit">
				<div class="g-pay-audio-library-edit__toolbar">
					<span v-if="form.items.length" class="g-pay-audio-library-edit__hint">音频支持拖拽排序</span>
					<div class="g-pay-audio-library-edit__toolbar-main">
						<span class="g-pay-audio-library-edit__count">已添加 {{ form.items.length }} 首音频</span>
						<button
							type="button"
							class="g-pay-audio-library-edit__upload-btn"
							:disabled="isUploading"
							@click="onAddAudios"
						>继续上传</button>
					</div>
				</div>

				<EditListSortGroup ref="sortList" class="g-pay-audio-library-edit__list" @dragover="onSortDragOver">
					<div
						v-for="(item, index) in form.items"
						:key="item.id"
						:data-sort-id="item.id"
						class="g-pay-audio-library-edit__item"
						:class="getEditListSortItemClass(dragId, item.id, dragOverId)"
						:draggable="!isActiveUploadItem(index) && !item.uploading"
						@dragstart="onDragStart(item.id)"
						@dragend="onDragEnd"
					>
						<div class="g-pay-audio-library-edit__cover">
							<UploadProgressPanel
								v-if="isActiveUploadItem(index)"
								:tasks="uploadTasks"
								variant="item"
								hide-filename
								status-text=""
							/>
							<template v-else>
								<img v-if="getCoverUrl(item)" :src="getCoverUrl(item)" alt="" />
								<i v-else class="mbm-iconfont mbm-yinpin"></i>
							</template>
						</div>
						<input
							v-model="item.audio.filename"
							type="text"
							class="g-pay-audio-library-edit__name-input"
							maxlength="40"
							placeholder="歌曲名称"
						/>
						<div class="g-pay-audio-library-edit__actions">
							<button
								type="button"
								class="g-pay-audio-library-edit__action-btn"
								@click="playEditMedia(getAudioUrl(item), 'audio')"
							>试听</button>
							<button type="button" class="g-pay-audio-library-edit__action-btn" @click="onReplaceAudio(index)">更换</button>
							<button type="button" class="g-pay-audio-library-edit__action-btn" @click="onReplaceCover(index)">换封面</button>
							<button
								type="button"
								class="g-pay-audio-library-edit__action-btn is-delete"
								:disabled="form.items.length <= 1"
								@click="onDeleteItem(index)"
							>删除</button>
						</div>
					</div>
					<div
						v-if="isUploading && uploadMode === 'append'"
						class="g-pay-audio-library-edit__item g-pay-audio-library-edit__item--append-upload"
					>
						<div class="g-pay-audio-library-edit__cover">
							<UploadProgressPanel
								:tasks="uploadTasks"
								:merge="uploadTasks.length > 1"
								variant="item"
								hide-filename
								status-text=""
							/>
						</div>
						<span class="g-pay-audio-library-edit__name-input g-pay-audio-library-edit__name-input--placeholder">上传中…</span>
					</div>
					<div v-if="!form.items.length && !isUploading" class="g-pay-audio-library-edit__empty">
						请上传音频文件
					</div>
				</EditListSortGroup>
			</div>
		</div>

		<input
			ref="coverInput"
			type="file"
			accept="image/*"
			class="g-pay-audio-library-edit__file-input"
			@change="onCoverSelect"
		/>
		<input
			ref="audioInput"
			type="file"
			accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.wma"
			class="g-pay-audio-library-edit__file-input"
			multiple
			@change="onAudioSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" :disabled="!form.items.length || isUploading" @click="onConfirm">确认</a-button>
		</template>

		<EditMediaPlayerOverlay
			:url="editPlayingUrl"
			:kind="editPlayingKind"
			@close="stopEditMediaPlay"
		/>
	</a-modal>
</template>

<script>
	import PayMediaEditForm from './PayMediaEditForm.vue';
	import EditMediaPlayerOverlay from './EditMediaPlayerOverlay.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import editModalMediaPlay from '../mixins/editModalMediaPlay.js';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
	} from '../utils/localImage.js';
	import { filterValidLocalAudioList } from '../utils/localAudio.js';
	import {
		createDefaultAudioLibraryItem,
		getAudioLibraryAudioUrl,
		getAudioLibraryCoverUrl,
		replaceAudioLibraryAudio,
		replaceAudioLibraryCover,
		revokeAudioLibraryItems,
	} from '../utils/audioLibrary.js';
	import {
		getPayAudioLibraryDefaults,
		normalizePayAudioLibraryData,
	} from '../utils/payAudioLibrary.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import {
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
	} from '../utils/editListDragSort.js';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GPayAudioLibraryEditModal',
		components: { EditListSortGroup, EditMediaPlayerOverlay, PayMediaEditForm, UploadProgressPanel },
		mixins: [editModalMediaPlay],
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => getPayAudioLibraryDefaults(),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: getPayAudioLibraryDefaults(),
				isUploading: false,
				uploadTasks: [],
				uploadMode: 'append',
				replaceIndex: -1,
				coverReplaceIndex: -1,
				replaceUploadIndex: -1,
				dragId: '',
				dragOverId: '',
			};
		},
		watch: {
			visible(value) {
				if (value) {
					this.initForm();
				}
			},
		},
		beforeUnmount() {
			revokeAudioLibraryItems(this.form.items);
		},
		methods: {
			getEditListSortItemClass,
			initForm() {
				const source = normalizePayAudioLibraryData(this.data);
				this.form = {
					...source,
					items: source.items.map(item => ({ ...item })),
				};
			},
			getCoverUrl(item) {
				return getAudioLibraryCoverUrl(item);
			},
			getAudioUrl(item) {
				return getAudioLibraryAudioUrl(item);
			},
			createLibraryItemFromAudio(audio, index) {
				const item = createDefaultAudioLibraryItem({}, index);
				return {
					...item,
					audio: replaceAudioLibraryAudio(item.audio, audio),
				};
			},
			isActiveUploadItem(index) {
				return this.isUploading
					&& this.uploadMode === 'replace'
					&& index === this.replaceUploadIndex;
			},
			clearUploadState() {
				this.isUploading = false;
				this.uploadTasks = [];
				this.uploadMode = 'append';
				this.replaceIndex = -1;
				this.replaceUploadIndex = -1;
			},
			async uploadAudios(files, { mode = 'append', replaceIndex = -1 } = {}) {
				this.isUploading = true;
				this.uploadMode = mode;
				this.uploadTasks = createUploadTasks(files);

				if (mode === 'replace') {
					this.replaceUploadIndex = replaceIndex;
					this.form.items[replaceIndex].uploading = true;
				}

				try {
					const items = await uploadHeaderMediaFiles(this, {
						type: 'audio',
						files,
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (mode === 'append') {
						const startIndex = this.form.items.length;
						this.form.items.push(
							...items.map((audio, index) => this.createLibraryItemFromAudio(audio, startIndex + index)),
						);
					} else if (mode === 'replace') {
						const old = this.form.items[replaceIndex];
						const next = this.createLibraryItemFromAudio(items[0], replaceIndex);
						next.cover = old.cover;
						next.id = old.id;
						revokeAudioLibraryItems([old]);
						this.form.items.splice(replaceIndex, 1, next);
					}
				} catch (error) {
					if (mode === 'replace' && this.form.items[replaceIndex]) {
						this.form.items[replaceIndex].uploading = false;
					}
					this.$message?.error?.('上传失败');
				} finally {
					this.clearUploadState();
				}
			},
			onAddAudios() {
				this.replaceIndex = -1;
				this.$refs.audioInput.value = '';
				this.$refs.audioInput.click();
			},
			onReplaceAudio(index) {
				this.replaceIndex = index;
				this.$refs.audioInput.value = '';
				this.$refs.audioInput.click();
			},
			onReplaceCover(index) {
				this.coverReplaceIndex = index;
				this.$refs.coverInput.value = '';
				this.$refs.coverInput.click();
			},
			onDeleteItem(index) {
				if (this.form.items.length <= 1) return;
				const [removed] = this.form.items.splice(index, 1);
				revokeAudioLibraryItems([removed]);
			},
			onDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.sortList, this);
			},
			onDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.sortList, this.form.items, this);
			},
			async onAudioSelect(event) {
				const files = Array.from(event.target.files || []);
				event.target.value = '';
				if (!files.length) return;
				const validFiles = filterValidLocalAudioList(files, files.length);
				if (!validFiles.length) return;
				if (this.replaceIndex >= 0) {
					const replaceIndex = this.replaceIndex;
					this.replaceIndex = -1;
					await this.uploadAudios(validFiles.slice(0, 1), { mode: 'replace', replaceIndex });
					return;
				}
				await this.uploadAudios(validFiles, { mode: 'append' });
			},
			async onCoverSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file) || this.coverReplaceIndex < 0) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				const item = this.form.items[this.coverReplaceIndex];
				if (!item) return;
				const coverReplaceIndex = this.coverReplaceIndex;
				this.coverReplaceIndex = -1;
				this.isUploading = true;
				this.uploadMode = 'replace';
				this.replaceUploadIndex = coverReplaceIndex;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [coverItem] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					item.cover = replaceAudioLibraryCover(item.cover, file, () => coverItem);
				} catch (error) {
					this.$message?.error?.('上传失败');
					this.coverReplaceIndex = coverReplaceIndex;
				} finally {
					this.clearUploadState();
				}
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				this.$emit('confirm', normalizePayAudioLibraryData({ ...this.form }));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-audio-library-edit-form {
		margin-top: 4px;
	}

	.g-pay-audio-library-edit {
		margin-top: 8px;
		padding-top: 8px;
	}

	.g-pay-audio-library-edit__toolbar {
		.edit-list-sort-toolbar-row();
		margin-bottom: 10px;
	}

	.g-pay-audio-library-edit__toolbar-main {
		.edit-list-sort-toolbar-main();
	}

	.g-pay-audio-library-edit__count {
		font-size: 13px;
		color: #666;
	}

	.g-pay-audio-library-edit__upload-btn {
		padding: 0;
		border: none;
		background: transparent;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;
		outline: none;

		&:disabled {
			color: #bbb;
			cursor: not-allowed;
		}
	}

	.g-pay-audio-library-edit__list {
		display: flex;
		flex-direction: column;
		gap: 10px;
		max-height: 360px;
		overflow-y: auto;
		padding-right: 4px;
	}

	.g-pay-audio-library-edit__item {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px;
		border: 1px solid #f0f0f0;
		border-radius: 10px;
		background: #fff;
		box-sizing: border-box;
		cursor: grab;

		&:active {
			cursor: grabbing;
		}

		&--append-upload {
			cursor: default;
		}
	}

	.g-pay-audio-library-edit__cover {
		position: relative;
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border-radius: 8px;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		i {
			font-size: 20px;
			color: #bfbfbf;
		}
	}

	.g-pay-audio-library-edit__name-input {
		flex: 1;
		min-width: 0;
		padding: 0;
		border: none;
		background: transparent;
		font-size: 14px;
		font-weight: 600;
		color: #333;
		line-height: 1.4;
		outline: none;

		&::placeholder {
			color: #bbb;
			font-weight: normal;
		}

		&--placeholder {
			color: #999;
			font-weight: normal;
		}
	}

	.g-pay-audio-library-edit__actions {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}

	.g-pay-audio-library-edit__action-btn {
		padding: 0;
		border: none;
		background: transparent;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;
		outline: none;

		&:disabled {
			color: #ddd;
			cursor: not-allowed;
		}
	}

	.g-pay-audio-library-edit__empty {
		padding: 28px 12px;
		text-align: center;
		font-size: 13px;
		color: #999;
		background: #fafafa;
		border-radius: 8px;
	}

	.g-pay-audio-library-edit__hint {
		.edit-list-sort-hint-text();
	}

	.g-pay-audio-library-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-audio-library-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();
	}
</style>
